import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import useNewCardsAPI from "../api/cardsAPI";
import { setFeatureData } from "../store/slices/appSlice";
import { ACTION_TYPES } from "../../app/actionTypes";
import {
  setActionPayload,
  setActionType,
  setStatus,
  setValidationInfo,
} from "../../features/authorizationSlice";
import {
  setFlag,
  setIsPopup,
  setResponseDescription,
  setResponseInfo,
  setResponseTitle,
} from "../../features/responseSlice";
import { decryptCardDetailData } from "../utils/cardDetailCrypto";

const CARD_DETAIL_EVENT_CATEGORY = "CardDetail";
const CARD_DETAIL_EVENT = "Refresh GetCardDetailData API";
const CARD_REQUEST_EVENT_CATEGORY = "CardRequest";
const CARD_REQUEST_EVENT = "CreateCardRequest API";
const EMPTY_CARD_ID = "00000000-0000-0000-0000-000000000000";

const CARD_DETAIL_SEQUENCE = [
  { Type: 1, LastCall: 0, field: "CardNumber" },
  { Type: 2, LastCall: 0, field: "ExpiryDate" },
  { Type: 3, LastCall: 1, field: "CVC" },
];

function parseSocketPayload(payload) {
  if (!payload) return null;
  if (typeof payload === "object") return payload;

  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

function extractEncryptedData(response) {
  return (
    response?.Payload?.EncryptedData ||
    response?.payload?.encryptedData ||
    response?.Payload?.encryptedData ||
    response?.payload?.EncryptedData ||
    response?.EncryptedData ||
    response?.encryptedData ||
    response?.data?.Payload?.EncryptedData ||
    response?.data?.payload?.encryptedData ||
    ""
  );
}

function normalizeCardDetailDisplayValue(value) {
  const normalizedValue = String(value ?? "").trim();
  if (!normalizedValue || normalizedValue.toLowerCase() === "null") return "--";
  return normalizedValue;
}

function normalizeDecryptedValue(value, field) {
  const rawValue = String(value ?? "").trim();
  if (!rawValue || rawValue.toLowerCase() === "null") return "--";

  // Plain digit strings (full card numbers, CVCs) must stay strings - routing
  // them through JSON.parse would coerce them to a Number and can silently
  // lose precision for 16-digit values near Number.MAX_SAFE_INTEGER.
  if (/^\d+$/.test(rawValue)) {
    return normalizeCardDetailDisplayValue(rawValue);
  }

  try {
    const parsedValue = JSON.parse(rawValue);
    if (parsedValue === null) return "--";

    if (typeof parsedValue === "string" || typeof parsedValue === "number") {
      return normalizeCardDetailDisplayValue(parsedValue);
    }

    if (parsedValue && typeof parsedValue === "object") {
      const normalizedValue =
        parsedValue[field] ??
        parsedValue[field.charAt(0).toLowerCase() + field.slice(1)] ??
        parsedValue.Value ??
        parsedValue.value;

      return normalizeCardDetailDisplayValue(normalizedValue);
    }
  } catch {
    // Plain decrypted strings are expected for the current card detail API.
  }

  return normalizeCardDetailDisplayValue(rawValue);
}

function getErrorMessage(error) {
  return (
    error?.response?.data?.PopupDescription ||
    error?.response?.data?.Message ||
    error?.response?.data?.message ||
    error?.message ||
    "We could not reveal the card details right now. Please try again."
  );
}

function maskCardNumber(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  const lastFour = digits.slice(-4);
  return lastFour ? `**** **** **** ${lastFour}` : "**** **** **** ****";
}

function isActiveCardStatus(status) {
  return String(status ?? "").trim().toLowerCase() === "active";
}

export function isCardDetailRefreshMessage(message) {
  return (
    message?.EventCategory === CARD_DETAIL_EVENT_CATEGORY &&
    message?.Event === CARD_DETAIL_EVENT
  );
}

export function isCardRequestMessage(message) {
  return (
    message?.EventCategory === CARD_REQUEST_EVENT_CATEGORY &&
    message?.Event === CARD_REQUEST_EVENT
  );
}

function getSocketSearchText(message, payload, nextAction) {
  return [
    message?.EventCategory,
    message?.Event,
    message?.Type,
    message?.Action,
    message?.Status,
    payload?.EventCategory,
    payload?.Event,
    payload?.Type,
    payload?.Action,
    payload?.Status,
    payload?.Message,
    payload?.AuthorizationStatus,
    payload?.QrStatus,
    payload?.ActionType,
    nextAction?.EventCategory,
    nextAction?.Event,
    nextAction?.Type,
    nextAction?.Action,
    nextAction?.Status,
    nextAction?.Message,
    nextAction?.AuthorizationStatus,
    nextAction?.QrStatus,
    nextAction?.ActionType,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isVirtualCardAuthorizationMessage(message) {
  if (!message || isCardRequestMessage(message)) {
    return false;
  }

  const payload = parseSocketPayload(message.Payload) || {};

  // The virtual-card backend currently sends the CardDetail decrypt key right
  // after QR approval. That key is also the signal that the request can proceed.
  if (isCardDetailRefreshMessage(message)) {
    return Boolean(payload?.Key);
  }

  const nextAction = parseSocketPayload(message.NextAction) || {};
  const searchText = getSocketSearchText(message, payload, nextAction);

  const hasAuthorizationCode = Boolean(
    payload.AuthorizationCode ||
      payload.authorizationCode ||
      nextAction.AuthorizationCode ||
      nextAction.authorizationCode,
  );
  const isRequestCardAction = [
    payload.ActionType,
    payload.actionType,
    nextAction.ActionType,
    nextAction.actionType,
  ].some((value) => value === ACTION_TYPES.REQUEST_CARD);
  const isAuthorizationRelated =
    /(authorization|authorisation|authorized|authorised|qr|portalaction|portal action|validation)/i.test(
      searchText,
    );
  const isSuccessfulAuthorization =
    /(authorized|authorised|complete|completed|success|approved|validated|scanned)/i.test(
      searchText,
    );

  return (
    (isAuthorizationRelated && isSuccessfulAuthorization) ||
    (hasAuthorizationCode && isRequestCardAction)
  );
}

export default function useCardDetailsRevealFlow() {
  const dispatch = useDispatch();
  const store = useStore();
  const { GetCardDetailData } = useNewCardsAPI();
  const cardDetailState = useSelector(
    (state) => state.app?.details?.corporateCards || {},
  );

  const selectedCardIdRef = useRef(cardDetailState.selectedCardIdForReveal);
  const awaitingRef = useRef(Boolean(cardDetailState.isAwaitingCardDetails));
  const fetchingRef = useRef(Boolean(cardDetailState.isFetchingCardDetails));
  const revealedCardsRef = useRef(cardDetailState.revealedCards || {});

  useEffect(() => {
    selectedCardIdRef.current = cardDetailState.selectedCardIdForReveal;
    awaitingRef.current = Boolean(cardDetailState.isAwaitingCardDetails);
    fetchingRef.current = Boolean(cardDetailState.isFetchingCardDetails);
    revealedCardsRef.current = cardDetailState.revealedCards || {};
  }, [
    cardDetailState.isAwaitingCardDetails,
    cardDetailState.isFetchingCardDetails,
    cardDetailState.revealedCards,
    cardDetailState.selectedCardIdForReveal,
  ]);

  const showRevealError = useCallback(
    (error) => {
      dispatch(setFlag(435));
      dispatch(setResponseTitle("Unable to Reveal Card Details"));
      dispatch(setResponseDescription(getErrorMessage(error)));
      dispatch(setResponseInfo({ forceWarningIcon: true }));
      dispatch(setIsPopup(true));
    },
    [dispatch],
  );

  const showCardRequestError = useCallback(
    (message) => {
      const normalizedMessage = String(message || "").trim();
      const isInsufficientFunds = /insufficient/i.test(normalizedMessage);

      dispatch(setFlag(isInsufficientFunds ? 90 : 435));
      dispatch(
        setResponseTitle(
          isInsufficientFunds
            ? "Insufficient Wallet Balance"
            : "Disposable Card Creation Failed",
        ),
      );
      dispatch(
        setResponseDescription(
          isInsufficientFunds
            ? "Your wallet balance is insufficient. Please top up your wallet or cancel."
            : normalizedMessage ||
                "We could not create the disposable card right now. Please try again.",
        ),
      );
      dispatch(setResponseInfo({ forceWarningIcon: true }));
      dispatch(setIsPopup(true));
    },
    [dispatch],
  );

  const resetRevealState = useCallback(
    (extra = {}) => {
      fetchingRef.current = false;
      awaitingRef.current = false;
      selectedCardIdRef.current = extra.selectedCardIdForReveal || null;

      dispatch(
        setFeatureData({
          section: "details",
          key: "corporateCards",
          value: {
            activeRevealedCardId: null,
            cardDetailDecryptKey: null,
            cardDetailError: null,
            cardDetailSocketPayload: null,
            isAwaitingCardDetails: false,
            isFetchingCardDetails: false,
            hasReceivedVirtualCardQrAuthorization: false,
            isWaitingForVirtualCardQrAuthorization: false,
            pendingCardDetailRequest: false,
            pendingVirtualCardRequestPayload: null,
            revealExpiresAt: null,
            selectedCardIdForReveal: null,
            ...extra,
          },
        }),
      );
    },
    [dispatch],
  );

  const hideRevealedCardDetails = useCallback(() => {
    dispatch(
      setFeatureData({
        section: "details",
        key: "corporateCards",
        value: {
          activeRevealedCardId: null,
          cardDetailDecryptKey: null,
          cardDetailSocketPayload: null,
          isAwaitingCardDetails: false,
          isFetchingCardDetails: false,
          pendingCardDetailRequest: false,
          revealExpiresAt: null,
          revealedCards: {},
          selectedCardIdForReveal: null,
        },
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (
      !cardDetailState.activeRevealedCardId ||
      !cardDetailState.revealExpiresAt
    ) {
      return undefined;
    }

    const timer = window.setTimeout(
      hideRevealedCardDetails,
      Math.max(0, cardDetailState.revealExpiresAt - Date.now()),
    );

    return () => window.clearTimeout(timer);
  }, [
    cardDetailState.activeRevealedCardId,
    cardDetailState.revealExpiresAt,
    hideRevealedCardDetails,
  ]);

  const requestCardRevealValidation = useCallback(
    (card, onOpenQrAfterGenerate, options = {}) => {
      const latestState = store.getState()?.app?.details?.corporateCards || {};
      const cardId = card?.cardId || card?.CardID || card?.id;
      const status = card?.status || card?.Status;
      const allowNonActiveCard = options.allowNonActiveCard === true;

      if (
        !cardId ||
        latestState.isAwaitingCardDetails ||
        latestState.isFetchingCardDetails ||
        (!allowNonActiveCard && !isActiveCardStatus(status))
      ) {
        return false;
      }

      dispatch(
        setFeatureData({
          section: "details",
          key: "corporateCards",
          value: {
            activeRevealedCardId: null,
            cardDetailDecryptKey: null,
            cardDetailError: null,
            cardDetailSocketPayload: null,
            isAwaitingCardDetails: true,
            isFetchingCardDetails: false,
            pendingCardDetailRequest: true,
            revealExpiresAt: null,
            revealedCards: {},
            selectedCardIdForReveal: cardId,
          },
        }),
      );
      dispatch(setActionType(""));
      dispatch(setActionPayload(null));
      dispatch(
        setValidationInfo({
          title: "Action Needed",
          profile: {
            label: "Card Name",
            name:
              card?.cardName || card?.CardName || card?.EmbossName || "-",
          },
          listings: [
            {
              label: "Action",
              value: "Validation is required to view card details.",
            },
            {
              label: "Card",
              value: maskCardNumber(card?.cardNumber || card?.CardNumber),
            },
          ],
        }),
      );
      onOpenQrAfterGenerate?.({ typeId: 12, additionalData: {} });
      return true;
    },
    [dispatch, store],
  );

  const revealCardDetails = useCallback(
    async ({ decryptKey, socketPayload, selectedCardId, latestCardDetailState }) => {
      try {
        fetchingRef.current = true;
        awaitingRef.current = true;
        dispatch(
          setFeatureData({
            section: "details",
            key: "corporateCards",
            value: {
              cardDetailDecryptKey: decryptKey,
              cardDetailSocketPayload: socketPayload,
              cardDetailError: null,
              isFetchingCardDetails: true,
            },
          }),
        );

        const revealedValues = {};
        const encryptedValues = {};

        for (const request of CARD_DETAIL_SEQUENCE) {
          const requestPayload = {
            Type: request.Type,
            LastCall: request.LastCall,
            CardId: selectedCardId,
          };

          const responsePayload = await GetCardDetailData(requestPayload);

          const encryptedData = extractEncryptedData(responsePayload);
          encryptedValues[request.field] = encryptedData;

          if (!encryptedData) {
            throw new Error("Card detail response is missing encrypted data.");
          }

          let decryptedRaw;
          try {
            decryptedRaw = decryptCardDetailData(encryptedData, decryptKey);
          } catch (decryptError) {
            throw decryptError;
          }

          const normalizedDecryptedValue = normalizeDecryptedValue(
            decryptedRaw,
            request.field,
          );
          revealedValues[request.field] = normalizedDecryptedValue;

        }


        const nextRevealedCards = {
          ...(latestCardDetailState.revealedCards || revealedCardsRef.current),
          [selectedCardId]: {
            ...((latestCardDetailState.revealedCards ||
              revealedCardsRef.current)?.[selectedCardId] || {}),
            ...revealedValues,
          },
        };
        revealedCardsRef.current = nextRevealedCards;

        resetRevealState({
          activeRevealedCardId: selectedCardId,
          cardDetailEncryptedPayloads: encryptedValues,
          cardDetailError: null,
          pendingCardDetailRequest: false,
          revealExpiresAt: Date.now() + 30000,
          revealedCards: nextRevealedCards,
        });

        return true;
      } catch (error) {
        showRevealError(error);
        resetRevealState({
          cardDetailError: getErrorMessage(error),
          pendingCardDetailRequest: false,
          revealExpiresAt: null,
        });
        return false;
      }
    },
    [GetCardDetailData, dispatch, resetRevealState, showRevealError],
  );

  const processCardDetailSocketEvent = useCallback(
    async (message) => {
      if (!isCardDetailRefreshMessage(message)) return false;

      const latestCardDetailState =
        store.getState()?.app?.details?.corporateCards || {};
      const selectedCardId =
        latestCardDetailState.selectedCardIdForReveal ||
        selectedCardIdRef.current;
      const isAwaitingCardDetails = Boolean(
        latestCardDetailState.isAwaitingCardDetails ?? awaitingRef.current,
      );
      const isFetchingCardDetails = Boolean(
        latestCardDetailState.isFetchingCardDetails ?? fetchingRef.current,
      );
      const createdDisposableCard = latestCardDetailState.createdDisposableCard;
      const isDisposableCardCreationInProgress = Boolean(
        createdDisposableCard &&
          ["awaiting-authorization", "requesting", "loading"].includes(
            createdDisposableCard.status,
          ),
      );

      const payload = parseSocketPayload(message.Payload);
      const decryptKey = payload?.Key;

      if (isFetchingCardDetails) {
        return false;
      }

      if (!isAwaitingCardDetails && !isDisposableCardCreationInProgress) {
        return false;
      }

      if (!decryptKey) {
        showRevealError(new Error("Card detail refresh key is missing."));
        resetRevealState({
          cardDetailError: "Card detail refresh key is missing.",
        });
        return false;
      }

      if (!selectedCardId) {
        dispatch(
          setFeatureData({
            section: "details",
            key: "corporateCards",
            value: {
              cardDetailDecryptKey: decryptKey,
              cardDetailSocketPayload: payload,
              cardDetailError: null,
            },
          }),
        );
        return true;
      }

      return revealCardDetails({
        decryptKey,
        socketPayload: payload,
        selectedCardId,
        latestCardDetailState,
      });
    },
    [dispatch, resetRevealState, revealCardDetails, showRevealError, store],
  );

  const processCardRequestSocketEvent = useCallback(
    async (message) => {
      if (!isCardRequestMessage(message)) return false;

      const payload = parseSocketPayload(message.Payload);
      const statusMessage = String(payload?.Message || "").toLowerCase();
      const rawFlagCode = payload?.FlagCode ?? payload?.flagCode;
      const hasFlagCode =
        rawFlagCode !== undefined &&
        rawFlagCode !== null &&
        String(rawFlagCode).trim() !== "";
      const flagCode = hasFlagCode ? Number(rawFlagCode) : null;
      const cardId =
        payload?.CardId ||
        payload?.CardID ||
        payload?.cardId ||
        payload?.cardID ||
        null;
      const normalizedCardId = String(cardId || "").trim().toLowerCase();
      const isFailedCardRequest =
        (hasFlagCode && (!Number.isFinite(flagCode) || flagCode !== 0)) ||
        ["failed", "failure", "error"].includes(statusMessage);

      const latestCardDetailState =
        store.getState()?.app?.details?.corporateCards || {};
      const createdDisposableCard = latestCardDetailState.createdDisposableCard;
      const isAwaitingDisposableCreation = Boolean(
        createdDisposableCard &&
          ["awaiting-authorization", "requesting", "loading"].includes(
            createdDisposableCard.status,
          ),
      );

      if (!isAwaitingDisposableCreation) {
        return false;
      }

      if (isFailedCardRequest) {
        const failureMessage =
          payload?.Message ||
          payload?.Error ||
          payload?.Reason ||
          "Disposable card creation failed.";

        showCardRequestError(failureMessage);
        resetRevealState({
          cardDetailError: failureMessage,
          createdDisposableCard: null,
          revealedCards: {},
        });
        return true;
      }

      if (!cardId || normalizedCardId === EMPTY_CARD_ID) {
        const missingCardMessage =
          payload?.Message && statusMessage !== "success"
            ? payload.Message
            : "The card was created, but no Card ID was returned.";

        showCardRequestError(missingCardMessage);
        resetRevealState({
          cardDetailError: missingCardMessage,
          createdDisposableCard: null,
          revealedCards: {},
        });
        return true;
      }

      if (
        !hasFlagCode &&
        statusMessage &&
        !["success", "succeeded", "completed"].includes(statusMessage)
      ) {
        return false;
      }

      const nextCreatedDisposableCard = {
        ...createdDisposableCard,
        cardId,
        socketPayload: payload,
        status: "loading",
      };

      dispatch(
        setFeatureData({
          section: "details",
          key: "corporateCards",
          value: {
            activeRevealedCardId: null,
            cardDetailError: null,
            createdDisposableCard: nextCreatedDisposableCard,
            isAwaitingCardDetails: true,
            pendingCardDetailRequest: true,
            revealExpiresAt: null,
            selectedCardIdForReveal: cardId,
          },
        }),
      );

      const storedDecryptKey = latestCardDetailState.cardDetailDecryptKey;
      if (!storedDecryptKey || latestCardDetailState.isFetchingCardDetails) {
        return true;
      }

      return revealCardDetails({
        decryptKey: storedDecryptKey,
        socketPayload: latestCardDetailState.cardDetailSocketPayload,
        selectedCardId: cardId,
        latestCardDetailState: {
          ...latestCardDetailState,
          createdDisposableCard: nextCreatedDisposableCard,
          selectedCardIdForReveal: cardId,
        },
      });
    },
    [
      dispatch,
      resetRevealState,
      revealCardDetails,
      showCardRequestError,
      store,
    ],
  );

  const processVirtualCardAuthorizationSocketEvent = useCallback(
    (message) => {
      const latestCardDetailState =
        store.getState()?.app?.details?.corporateCards || {};
      const createdDisposableCard = latestCardDetailState.createdDisposableCard;
      const isWaitingForAuthorization = Boolean(
        latestCardDetailState.isWaitingForVirtualCardQrAuthorization ||
          createdDisposableCard?.status === "awaiting-authorization",
      );

      if (!createdDisposableCard || !isWaitingForAuthorization) {
        return false;
      }

      if (createdDisposableCard.status !== "awaiting-authorization") {
        return false;
      }

      if (!isVirtualCardAuthorizationMessage(message)) {
        return false;
      }

      const requestPayload =
        latestCardDetailState.pendingVirtualCardRequestPayload ||
        createdDisposableCard.requestPayload;

      if (!requestPayload) {
        showCardRequestError(
          "The virtual card request is missing. Please try again.",
        );
        resetRevealState({
          cardDetailError: "The virtual card request is missing.",
          createdDisposableCard: null,
          revealedCards: {},
        });
        return true;
      }

      dispatch(
        setFeatureData({
          section: "details",
          key: "corporateCards",
          value: {
            createdDisposableCard: {
              ...createdDisposableCard,
              requestPayload,
              status: "requesting",
            },
            hasReceivedVirtualCardQrAuthorization: true,
            isWaitingForVirtualCardQrAuthorization: false,
            pendingVirtualCardRequestPayload: requestPayload,
          },
        }),
      );

      dispatch(setActionPayload(requestPayload));
      dispatch(setActionType(ACTION_TYPES.REQUEST_CARD));
      dispatch(setStatus("AUTHORIZED"));

      return true;
    },
    [dispatch, resetRevealState, showCardRequestError, store],
  );

  return {
    processCardDetailSocketEvent,
    processCardRequestSocketEvent,
    processVirtualCardAuthorizationSocketEvent,
    requestCardRevealValidation,
  };
}
